import { NavigateFunction } from 'react-router'
import { Privileges } from '@reapit-sales/api-v2'
import { NavigationMenu } from '@reapit-sales/navigation'
import { NAV_MENU, NavTitle } from '../../constants'
import { buildDashboardPageURL } from '../../../components/dashboard/buildDashboardPageURL'
import { trackMixpanelAction, NAVIGATION } from '@reapit-sales/utilities'
import { getMyOfficesMenu } from './getMyOfficesMenu'

/**
 * Generates the main logo menu configuration based on user privileges.
 *
 * @param privileges - The user's privilege set (from APIv2 GET/configurations).
 * @param navigate - The `react-router` navigate function for handling programmatic navigation.
 * @param legacyCRMIframePageName - The current legacy CRM iframe page name (used for tracking).
 *
 * @returns A `NavigationMenu` object.
 *
 * Behavior:
 * - If the user has access to the dashboard (`privileges.dashboard.canAccess`):
 *   - Creates a main navigation menu item for the Dashboard.
 *   - Attaches a Mixpanel tracking event for dashboard navigation.
 *   - Includes an `href` to the dashboard page.
 * - Otherwise:
 *   - Returns the "My Offices" menu configuration as a fallback.
 */
export const getMainLogoMenu = (
  privileges: Privileges,
  navigate: NavigateFunction,
  legacyCRMIframePageName: string,
): NavigationMenu => {
  let defaultConfig = {
    itemIndex: 1,
    title: NAV_MENU.DASHBOARD as NavTitle,
    hasAccess: privileges.dashboard?.canAccess,
    onClick: () => {
      // Track the Mixpanel event for navigating to Dashboard
      trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_DASHBOARD, {
        DestinationRoute: buildDashboardPageURL(),
        FromRoute: legacyCRMIframePageName ?? '',
      })
    },
    href: buildDashboardPageURL(),
  }
  if (!privileges.dashboard.canAccess) {
    const officeMenuConf = getMyOfficesMenu(navigate, legacyCRMIframePageName)
    // reset @defaultConfig and set config as office menu
    defaultConfig = {
      ...defaultConfig,
      ...officeMenuConf,
      title: officeMenuConf.label as NavTitle,
    }
  }

  return defaultConfig
}
